import React, { useCallback, useContext } from 'react';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import {
  TextAssetCopilot,
  TextAssetType,
} from '@bingads-webui/inline-copilot-ui';

import { useCopilotRepositoryContext } from '../CopilotRepositoryProvider';
import {
  AssetType,
} from '../../types';

type RequestType = 'rewrite' | 'differentTone' | 'findSimilar'; // TODO: need to be consistent after replacement

interface ITextAsset {
  text: string;
  tone?: string;
}

export interface CopilotTextAssetRecommendationProps {
  assetType: TextAssetType;
  currentIndex: number;
  existingAssets: {
    shortHeadlines?: string[],
    longHeadlines?: string[],
    descriptions?: string[],
  }
  disableReason?: string;
  menuButtonAriaLabel?: string;
  onSelect: (param: { value: ITextAsset[] }) => void;
  onClose?: (dialogType: RequestType) => void;
  onSuggestionsRendered?: (finalUrl: string, params: any, assets: ITextAsset[]) => void;
  onToneSelected?: (tone: string) => void;
  onFetch?: (assetType: AssetType, operationName: string) => void,
  onMenuOpen?: (assetType: TextAssetType) => void;
  onOperationDialogOpen?: (assetType: TextAssetType, operationName: string) => void,
  isNewLogo?: boolean,
}

export const CopilotTextAssetRecommendation: React.FC<CopilotTextAssetRecommendationProps> = (props) => {
  const {
    assetType,
    currentIndex,
    existingAssets,
    disableReason,
    menuButtonAriaLabel,
    onSelect,
    onClose,
    onSuggestionsRendered,
    onToneSelected,
    onFetch,
    onMenuOpen,
    onOperationDialogOpen,
    isNewLogo,
  } = props;

  const i18n = useContext(I18nContext) as I18nModel;
  const repository = useCopilotRepositoryContext();
  const { fetchRecommendationAssets, perfMarker } = repository || {};

  const fetchAssets = useCallback((params: any) => {
    const { requestType } = params;

    const fetchPerfMarker = perfMarker?.createChild(`fetchTextAssetRecommendation_${requestType}`);
    fetchPerfMarker?.willInit();

    const shortHeadlines = existingAssets.shortHeadlines || [];
    const longHeadlines = existingAssets.longHeadlines || [];
    const descriptions = existingAssets.descriptions || [];
    const prompts = params.value ? [params.value] : undefined;
    const tones = params.activeTone ? [params.activeTone] : undefined;

    const assetTypeEnumValue = assetType as AssetType;
    onFetch?.(assetTypeEnumValue, requestType);
    fetchPerfMarker?.willFetchData();
    return fetchRecommendationAssets?.(
      assetTypeEnumValue,
      requestType,
      shortHeadlines,
      longHeadlines,
      descriptions,
      currentIndex,
      prompts,
      tones,
    ).then((response) => {
      fetchPerfMarker?.willRender();
      return response;
    }).catch(() => {
      if (requestType === 'differentTone') {
        throw new Error(i18n.getString(_TL_('We don\'t have a recommendation in this tone.')));
      }
      if (requestType === 'rewrite') {
        throw new Error(i18n.getString(_TL_('We\'re not able to generate an asset from the provided text. Please enter a new description and try again.')));
      }
      throw new Error(i18n.getString(_TL_('There are no more recommendations.')));
    }).finally(() => {
      fetchPerfMarker?.done();
    });
  }, [
    assetType,
    currentIndex,
    existingAssets,
    fetchRecommendationAssets,
    i18n,
    onFetch,
    perfMarker,
  ]);

  if (!repository) {
    // repository not available, do not render
    return null;
  }

  return (
    <TextAssetCopilot
      assetType={assetType}
      finalUrl={repository?.finalUrl}
      currentAssetIndex={currentIndex}
      existingAssets={existingAssets}
      onSelect={onSelect}
      onClose={onClose}
      disableReason={disableReason}
      locale={i18n.getLocale()}
      menuButtonAriaLabel={menuButtonAriaLabel}
      // @ts-ignore
      fetchAssets={fetchAssets}
      onSuggestionsRendered={onSuggestionsRendered}
      onToneSelected={onToneSelected}
      onMenuOpen={onMenuOpen}
      onOperationDialogOpen={onOperationDialogOpen}
      isNewLogo={isNewLogo}
    />
  );
};
